import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { waitForStatePersistence } from "../../../services/redux/app/store";
import { checkFranchiseCartRedux } from "../../../services/redux/slices/cartAndLoyaltySlice";
import {
  removeItemAction,
  resetCartAction,
} from "../../../services/redux/slices/cartLoyaltyV2Slice";
import { selectCartItemsNumber } from "../../../services/redux/selectors/cartSelectors";
import { selectLoyaltyCartLines } from "../../../services/redux/selectors/loyaltyCartSelectors";
import { sortActiveFranchisesByDistance } from "../../../services/redux/slices/configSlice";
import {
  setAnimatedGlobalBannerState,
  setChangingRestaurant,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import { setClickDetected } from "../../../services/redux/slices/locationSlice";
import {
  CUSTOMER_ANALYTICS_EVENT,
  OrderTypesEnum,
  PagesEnum,
} from "../../../utils/enums";
import { getLineMenuItemId } from "../../../utils/cart/orderLineMenuIds";
import { getAllDisabledItems } from "../../../utils/help/itemDetails/itemDetails";
import {
  distanceText,
  getFranchiseDisplayName,
} from "../../../utils/help/localization/localization";
import {
  filterFranchisesByDirectLinkAccess,
  getPageLink,
} from "../../../utils/help/navigation/navigation";
import {
  Franchise,
  FranchiseWithDistance,
  NewQueryParamsType,
  RestaurantTypeEnum,
} from "../../../utils/types";
import { ArrowIcon } from "../../screens/S-Cart/Cart.icons";
import AutoDetectLocation from "../AutoDetectLocation/AutoDetectLocation";
import DetectLocation from "../DetectLocation/DetectLocation";
import { LocationIcon } from "../Icons/Icons";
import styles from "./LocationDropdown.module.scss";

export enum LocationDropdownType {
  DEFAULT = "default",
  SELECT_ORDER_INFO = "select_order_info",
}

export const LocationDropdown = (props: {
  handleBanner?: boolean;
  receiptFranchiseLocation?: string;
  setSelectedFranchise?: (franchise: Franchise) => void;
  selectedFranchise?: Franchise;
  setFranchiseOpened?: (opened: boolean) => void;
  franchiseOpened?: boolean;
  type?: LocationDropdownType;
}) => {
  const { type = LocationDropdownType.DEFAULT } = props;

  const dispatch = useAppDispatch();
  const { t } = useTranslationI18();
  const addCustomerAnalytics = useCustomerAnalytics();
  const [active, setActive] = useState(false);
  const router = useRouter();
  const { franchise, invalid_franchise } = router.query;
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const { handleModal } = useModalHook();

  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const activeFranchises = useAppSelector(
    (state) => state.config.activeFranchises
  );

  const filteredFranchises = filterFranchisesByDirectLinkAccess(
    activeFranchises,
    franchise as string
  );

  const cartAndLoyalty = useAppSelector((state) => state.cartAndLoyaltyReducer);
  const cartLines = useAppSelector(selectLoyaltyCartLines);
  const cartItemsNumber = useAppSelector(selectCartItemsNumber);
  const subItemLookup = useAppSelector(
    (state) => state.config.subItemLookupByUuid
  );
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );

  const restaurantType = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );

  const isEcommerce: boolean = restaurantType === RestaurantTypeEnum.ECOMMERCE;

  const isCartPage = router.pathname.includes(PagesEnum.Cart);
  const isTipPage = router.pathname.includes(PagesEnum.Tip);
  const isPayPage = router.pathname.includes(PagesEnum.Pay);

  const selectedFranchise = useAppSelector(
    (store) => store.config.selectedFranchise
  );

  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const chooseLocation = t("common:choose-location");
  const isTakeout = orderTypeParam === OrderTypesEnum.TAKE_OUT;

  const fromBranch = isTakeout
    ? t(
        "common:from-branch",
        {},
        router.locale,
        selectedFranchise?.franchiseConfig.takeOut?.customOrderTypeName,
        selectedFranchise?.franchiseConfig.takeOut?.localizedCustomOrderTypeName
      )
    : t(
        "common:from-branch",
        {},
        router.locale,
        selectedFranchise?.franchiseConfig.dineIn?.customOrderTypeName,
        selectedFranchise?.franchiseConfig.dineIn?.localizedCustomOrderTypeName
      );

  const checkCartForDisabledItems = () => {
    const allDisabledItemsIds = getAllDisabledItems(
      itemStatusRedux,
      orderTypeParam
    );
    if (allDisabledItemsIds.length === 0) {
      return;
    }
    const disabledCartItemsIndexes: number[] = [];
    cartLines.forEach((line, index) => {
      const itemId = getLineMenuItemId(
        line,
        subItemLookup ?? {},
        entityImageLookup ?? {}
      );
      if (itemId !== undefined && allDisabledItemsIds.includes(itemId)) {
        disabledCartItemsIndexes.push(index);
      }
    });
    if (disabledCartItemsIndexes.length === 0) {
      return;
    } else if (router.pathname.includes("tip")) {
      return router.push(getPageLink(router, PagesEnum.Cart).href);
    }
    if (disabledCartItemsIndexes.length > 0) {
      dispatch(
        removeItemAction({
          itemIndex: disabledCartItemsIndexes,
          useLoyaltyService: true,
        })
      );
      dispatch(
        setAnimatedGlobalBannerState({
          isOpen: true,
          text: t("cart:items-not-available"),
          warning: true,
        })
      );
    }
  };

  useEffect(() => {
    if (props.franchiseOpened !== undefined) {
      setActive(props.franchiseOpened);
    }
  }, [props.franchiseOpened]);

  useEffect(() => {
    if (restaurantInfo?.franchises) {
      initialize();
    }
  }, [dispatch, selectedFranchise, restaurantInfo?.franchises]);

  useEffect(() => {
    if (props && (isCartPage || isTipPage)) {
      checkCartForDisabledItems();
    }
  }, [franchise, cartItemsNumber, orderTypeParam]);

  const initialize = () => {
    if (invalid_franchise && invalid_franchise === "true") {
      dispatch(resetCartAction());
    }
  };

  const handleClick = (franchise: Franchise) => {
    if (type === LocationDropdownType.SELECT_ORDER_INFO) {
      props.setSelectedFranchise?.(franchise);
      setActive(false);
      props.setFranchiseOpened?.(false);

      return;
    }

    dispatch(setClickDetected(true));
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CHANGE_BRANCH_FROM_CHECKOUT, {
      franchiseId: franchise.id,
      franchiseSlug: franchise.slug,
    });
    let newQueryParams: NewQueryParamsType = {
      ...router.query,
    };
    delete newQueryParams.promo;
    if (franchise.slug) {
      newQueryParams = {
        ...newQueryParams,
        ...{ franchise: franchise.slug },
      };
    }

    if (selectedFranchise?.id !== franchise.id) {
      dispatch(checkFranchiseCartRedux(franchise.id));
      if (cartAndLoyalty.cart.franchiseId !== franchise.id) {
        dispatch(resetCartAction());
      }
      // if (isCartPage || isTipPage) {
      dispatch(setChangingRestaurant(true));

      waitForStatePersistence().then(() => {
        //  This is done using Href on purpose so that the page does a hard reload when we redirect direct to the home screen and all the (.json) objects is fetched again in order for the custom price tp be recalculated using the original build json object
        window.location.href = getPageLink(router, PagesEnum.Home, {
          orderType: orderTypeParam,
          tableNumber: +newQueryParams.table!,
          franchiseSlug:
            newQueryParams.franchise === undefined
              ? false
              : (newQueryParams.franchise as string),
        }).as;
      });
      // } else {
      //   router.push({ query: newQueryParams }, undefined, { shallow: true });
      // }
    }
    setActive(false);
  };

  const displayFranchiseName = (franchise?: Franchise) => {
    return getFranchiseDisplayName(franchise);
  };
  const showDropDown =
    bottomSheetModal.isOpen ||
    orderTypeParam !== OrderTypesEnum.DELIVERYV2 ||
    isPayPage;

  const isOnlyOneFranchise = filteredFranchises.length === 1;

  return !isEcommerce &&
    !props.setSelectedFranchise &&
    (isOnlyOneFranchise || (selectedFranchise && !selectedFranchise.live)) ? (
    <>
      {type === LocationDropdownType.SELECT_ORDER_INFO ? (
        <span className={styles.location} data-testid={"franchise_location"}>
          <LocationIcon />
          <span>
            {selectedFranchise || props.selectedFranchise
              ? displayFranchiseName(
                  selectedFranchise || props.selectedFranchise
                )
              : displayFranchiseName(filteredFranchises[0])}
          </span>
        </span>
      ) : (
        <>
          <div className={`${styles.wrapper} ${styles.cart_tip_version}`}>
            <div className={styles.select_label}>{fromBranch}:</div>

            <div
              className={`${styles.select} ${styles.not_clickable}`}
              data-testid={"franchise_location"}
            >
              <span className={styles.select_location_icon}>
                <LocationIcon />
              </span>
              <span
                className={`${styles.select_text} ${styles.select_text_md} ${
                  props.setSelectedFranchise && styles.min_height
                }`}
              >
                {selectedFranchise || props.selectedFranchise
                  ? displayFranchiseName(
                      selectedFranchise || props.selectedFranchise
                    )
                  : displayFranchiseName(filteredFranchises[0])}
              </span>
            </div>
          </div>
        </>
      )}
    </>
  ) : (
    <>
      {showDropDown ? (
        <div
          className={`${styles.wrapper} ${
            type === LocationDropdownType.DEFAULT && styles.cart_tip_version
          }`}
        >
          {type === LocationDropdownType.DEFAULT && (
            <div className={styles.select_label}>{fromBranch}:</div>
          )}
          {!isPayPage ? (
            <div
              className={`${styles.select} ${
                type === LocationDropdownType.DEFAULT
                  ? styles.with_shadow
                  : styles.with_dark_border
              } ${active && styles.active}`}
              onClick={() => {
                if (isOnlyOneFranchise) {
                  return;
                }
                setActive(!active);
                props.setFranchiseOpened?.(!active);
              }}
              data-testid={"franchise_location"}
            >
              <span className={styles.select_location_icon}>
                <LocationIcon />
              </span>
              <span
                className={`${styles.select_text} ${styles.select_text_md}`}
              >
                {displayFranchiseName(
                  props.selectedFranchise || selectedFranchise
                ) || chooseLocation}
              </span>
              {!isOnlyOneFranchise && (
                <span className={styles.select_arrow_icon}>
                  <ArrowIcon />
                </span>
              )}
            </div>
          ) : !isEcommerce ? (
            <div
              className={`${styles.select} ${styles.not_clickable}`}
              data-testid={"franchise_location"}
            >
              <span className={styles.select_location_icon}>
                <LocationIcon />
              </span>
              <span
                className={`${styles.select_text} ${styles.select_text_md}`}
              >
                {props.receiptFranchiseLocation
                  ? props.receiptFranchiseLocation
                  : displayFranchiseName(
                      props.selectedFranchise || selectedFranchise
                    ) || chooseLocation}
              </span>
            </div>
          ) : (
            <div></div>
          )}

          <ul
            className={`${styles.dropdown} ${active && styles.active_dropdown}`}
            data-testid={`dropdown-all-franchises`}
          >
            {filteredFranchises.map((fr: FranchiseWithDistance) => {
              return (
                <li
                  key={fr.id}
                  onClick={() => {
                    if (isCartPage || isTipPage) {
                      handleModal(true, "SwitchFranchise/SwitchFranchise", {
                        componentProps: {
                          franchise: fr,
                          returnPage: PagesEnum.Home,
                        },
                      });
                    } else {
                      handleClick(fr);
                    }
                  }}
                  className={`${
                    (props.selectedFranchise?.id || selectedFranchise?.id) ===
                      fr.id && styles.active_link
                  }`}
                >
                  <a data-testid={`dropdown-franchise-${fr.id}`}>
                    {displayFranchiseName(fr)}
                  </a>
                  <span
                    data-testid={`distance-from-loc-${fr.id}`}
                    className={styles.distance_from_location}
                  >
                    {distanceText(fr.distanceFromLocationInMeters, t)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <></>
      )}
      {type === LocationDropdownType.SELECT_ORDER_INFO &&
        restaurantInfo?.config?.detectNearestBranchLocation && (
          <DetectLocation
            onSuccess={(lngLat) => {
              setActive(true);
              props.setFranchiseOpened?.(true);
              dispatch(sortActiveFranchisesByDistance(lngLat));
            }}
          />
        )}
      {restaurantInfo?.config?.autoLocationDetection && (
        <AutoDetectLocation page={router.pathname.split("/")[1] as PagesEnum} />
      )}
    </>
  );
};
